import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, ArrowRight, Check, ChevronRight, CircleDot, Command, LockKeyhole, LogOut, Pause, Play, Radar, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import type { PortfolioSnapshot, PrimaryResearchTask, Venture } from "../shared/types";
import { valuePropFor } from "../shared/value-props";
import { api } from "./api";
import { VenturePage } from "./components/VenturePage";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const relativeTime = (date: string) => {
  const delta = Math.max(0, Date.now() - new Date(date).getTime());
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

function LoginScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(password);
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Access denied");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-shell">
      <div className="login-ambient" />
      <form className="login-card" onSubmit={submit}>
        <div className="brand-mark brand-mark-large"><Radar size={25} strokeWidth={1.7} /></div>
        <p className="eyebrow">Private venture operations</p>
        <h1>XWARM</h1>
        <p className="login-copy">Five evidence-gated businesses. One private command surface.</p>
        <label className="field-label" htmlFor="password">Access key</label>
        <div className="password-field">
          <LockKeyhole size={17} />
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter access key"
            autoFocus
          />
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button login-button" disabled={busy || !password}>
          {busy ? <RefreshCw className="spin" size={17} /> : <ArrowRight size={17} />}
          Open command center
        </button>
        <div className="login-safety"><ShieldCheck size={15} /> No public identity data is shown.</div>
      </form>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-shell">
      <div className="brand-mark"><Radar size={20} /></div>
      <span>Loading venture graph</span>
    </main>
  );
}

function ProgressRing({ value, color }: { value: number; color: string }) {
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, value)) / 100) * circumference;
  return (
    <div className="progress-ring" style={{ "--ring-color": color } as React.CSSProperties} aria-label={`${value}% complete`}>
      <svg viewBox="0 0 60 60" aria-hidden="true">
        <circle className="ring-track" cx="30" cy="30" r={radius} />
        <circle className="ring-value" cx="30" cy="30" r={radius} strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <span>{value}</span>
    </div>
  );
}

function MetricCard({ label, value, note, tone = "neutral" }: { label: string; value: string; note: string; tone?: "neutral" | "lime" }) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function VentureRow({ venture, index, replacementReview }: { venture: Venture; index: number; replacementReview: { eligible: boolean; reason: string } }) {
  const passed = venture.steps.filter((step) => step.status === "passed").length;
  const current = venture.steps.find((step) => step.number === venture.currentStep) || venture.steps[0];
  const progress = Math.round(((passed + (current?.progress || 0) / 100) / 24) * 100);
  const valueProp = valuePropFor(venture);
  return (
    <Link to={`/venture/${venture.slug}`} className="venture-row" style={{ "--accent": venture.accent } as React.CSSProperties}>
      <div className="venture-index">{String(venture.portfolioSlot || index + 1).padStart(2, "0")}</div>
      <ProgressRing value={progress} color={venture.accent} />
      <div className="venture-main">
        <div className="venture-title-line">
          <h3>{venture.name}</h3>
          <span className="status-pill"><span className="status-dot" />{venture.status}</span>
        </div>
        <div className="venture-value-prop">
          <p><strong>Problem</strong><span>{valueProp.problem}</span></p>
          <p><strong>Solution</strong><span>{valueProp.solution}</span></p>
        </div>
        <div className="venture-tags">
          <span>{venture.codename}</span>
          <span>slot {venture.portfolioSlot} · generation {venture.generation}</span>
          <span title={replacementReview.reason}>{replacementReview.eligible ? "replacement ready" : "replacement blocked"} · {replacementReview.reason}</span>
          <span>{venture.evidenceCount} sources</span>
          <span>{venture.activeJobCount ? `${venture.activeJobCount} agent running` : "agent ready"}</span>
        </div>
      </div>
      <div className="gate-column">
        <span className="data-label">Current gate</span>
        <strong>{String(venture.currentStep).padStart(2, "0")}/24</strong>
        <small>{current?.gateSummary.split(".")[0]}.</small>
      </div>
      <div className="confidence-column">
        <span className="data-label">Evidence confidence</span>
        <div className="confidence-bar"><i style={{ width: `${venture.confidence}%` }} /></div>
        <small>{venture.confidence}% secondary signal</small>
      </div>
      <div className="row-arrow"><ChevronRight size={22} /></div>
    </Link>
  );
}

type ApprovalItem = { venture: Venture; task: PrimaryResearchTask };

function ApprovalCockpit({ ventures, refreshKey }: { ventures: Venture[]; refreshKey: string }) {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [permissionItems, setPermissionItems] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [permissionCopied, setPermissionCopied] = useState(false);
  const ventureKey = ventures.map((venture) => venture.id).join(":");

  const copyReviewQueue = async () => {
    const packet = items.map(({ venture, task }, index) => [
      `STUDY ${index + 1} OF ${items.length}`,
      `Venture: ${venture.name} (slot ${venture.portfolioSlot}, generation ${venture.generation})`,
      `Study ID: ${task.id}`,
      `Segment: ${task.segmentRef}`,
      `Pseudonymous contact: ${task.contactRef}`,
      `Channel: ${task.channel}`,
      `Source: ${task.sourceUrl || "Not recorded"}`,
      `Lawful basis: ${task.lawfulBasis}`,
      `Channel permission: ${task.channelPermissionStatus}`,
      `Permission evidence: ${task.channelPermissionEvidenceUrl || "Not recorded"}`,
      `Permission note: ${task.channelPolicyNote}`,
      `DNC screening: ${task.dncStatus}`,
      `Campaign approval: ${task.campaignApproval}`,
      "",
      "Recruitment draft:",
      task.recruitmentMessage,
      "",
      "Consent opening:",
      task.consentScript,
      "",
      "Qualification questions:",
      ...task.qualificationQuestions.map((question, questionIndex) => `${questionIndex + 1}. ${question}`),
      "",
      `Evidence capture: ${task.captureFields.join(" | ")}`,
    ].join("\n")).join("\n\n---\n\n");
    await navigator.clipboard.writeText(packet);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const copyPermissionQueue = async () => {
    const packet = permissionItems.map(({ venture, task }, index) => [
      `PLATFORM SETUP ${index + 1} OF ${permissionItems.length}`,
      `Venture: ${venture.name} (slot ${venture.portfolioSlot}, generation ${venture.generation})`,
      `Study ID: ${task.id}`,
      `Segment: ${task.segmentRef}`,
      `Pseudonymous slot: ${task.contactRef}`,
      `Recruitment platform: ${task.sourceUrl}`,
      `Why this route: ${task.eligibilityNote}`,
      `Policy review: ${task.channelPolicyNote}`,
      "",
      "Recruitment draft:",
      task.recruitmentMessage,
      "",
      "Qualification questions:",
      ...task.qualificationQuestions.map((question, questionIndex) => `${questionIndex + 1}. ${question}`),
      "",
      "Controls: no scraping, no direct messages, no account creation, no spend, and no launch without owner approval.",
    ].join("\n")).join("\n\n---\n\n");
    await navigator.clipboard.writeText(packet);
    setPermissionCopied(true);
    window.setTimeout(() => setPermissionCopied(false), 1800);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(ventures.map(async (venture) => ({ venture, detail: await api.venture(venture.slug) })))
      .then((results) => {
        if (cancelled) return;
        const allTasks = results.flatMap(({ venture, detail }) => detail.primaryResearch.map((task) => ({ venture, task })));
        setItems(allTasks.filter(({ task }) => task.channelPermissionStatus === "granted" && task.dncStatus === "clear" && task.campaignApproval === "required"));
        setPermissionItems(allTasks.filter(({ task }) => task.channelPermissionStatus === "permission-required" && Boolean(task.sourceUrl)));
        setError("");
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "Could not load approval queue");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, ventureKey]);

  return (
    <section className="approval-cockpit" aria-label="Primary research approvals">
      <div className="approval-heading">
        <div><p className="eyebrow">Human action boundary</p><h2>Research launch approvals</h2><p>Only studies with documented channel permission and clear do-not-call screening appear here. Nothing launches until you review and approve it.</p></div>
        <button className="secondary-button" type="button" disabled={loading || items.length === 0} onClick={() => void copyReviewQueue()}>{copied ? "Review queue copied" : items.length ? `Copy all ${items.length} packets` : "Copy review queue"}</button>
        <span>{loading ? "Checking" : `${items.length} ready for review`}</span>
      </div>
      {error && <p className="approval-error">{error}</p>}
      {!loading && !error && (
        <div className="approval-list">
          {items.map(({ venture, task }) => (
            <Link to={`/venture/${venture.slug}?tab=research`} className="approval-row" key={task.id} style={{ "--accent": venture.accent } as React.CSSProperties}>
              <i className="approval-accent" />
              <div className="approval-venture"><strong>{venture.name}</strong><span>{task.segmentRef}</span></div>
              <div className="approval-channel"><span>Channel</span><strong>{task.channel}</strong></div>
              <div className="approval-controls"><span className="approval-control-ready">Permission granted</span><span className="approval-control-ready">DNC clear</span><span className="approval-control-hold">Approval required</span></div>
              <div className="approval-review">Review study <ChevronRight size={16} /></div>
            </Link>
          ))}
          {items.length === 0 && <div className="approval-empty"><ShieldCheck size={18} /><span>No launch-ready studies are waiting for approval.</span></div>}
        </div>
      )}
      <div className="approval-heading permission-queue-heading">
        <div><p className="eyebrow">Rotated research channels</p><h2>Platform setup queue</h2><p>Denied community routes stay dead. These opt-in research platforms need account, policy, incentive, and launch review before use.</p></div>
        <button className="secondary-button" type="button" disabled={loading || permissionItems.length === 0} onClick={() => void copyPermissionQueue()}>{permissionCopied ? "Setup queue copied" : permissionItems.length ? `Copy all ${permissionItems.length} setup packets` : "Copy setup queue"}</button>
        <span>{loading ? "Checking" : `${permissionItems.length} need setup`}</span>
      </div>
      {!loading && !error && (
        <div className="approval-list">
          {permissionItems.map(({ venture, task }) => (
            <Link to={`/venture/${venture.slug}?tab=research`} className="approval-row" key={task.id} style={{ "--accent": venture.accent } as React.CSSProperties}>
              <i className="approval-accent" />
              <div className="approval-venture"><strong>{venture.name}</strong><span>{task.segmentRef}</span></div>
              <div className="approval-channel"><span>Route</span><strong>opt-in panel</strong></div>
              <div className="approval-controls"><span className="approval-control-ready">Denied route killed</span><span className="approval-control-ready">Source reviewed</span><span className="approval-control-hold">Setup required</span></div>
              <div className="approval-review">Review setup <ChevronRight size={16} /></div>
            </Link>
          ))}
          {permissionItems.length === 0 && <div className="approval-empty"><ShieldCheck size={18} /><span>No platform setup packets are waiting.</span></div>}
        </div>
      )}
    </section>
  );
}

function PortfolioPage({ snapshot, refresh }: { snapshot: PortfolioSnapshot; refresh: () => void }) {
  const port = snapshot.portfolio;
  const [controlBusy, setControlBusy] = useState(false);
  const toggleRuntime = async () => {
    const action = snapshot.runtime.portfolioPaused ? "resume" : "pause";
    const reason = action === "pause" ? "Paused from the authenticated command center." : "Resumed from the authenticated command center.";
    setControlBusy(true);
    try {
      await api.setRuntime(action, reason);
      refresh();
    } finally {
      setControlBusy(false);
    }
  };
  return (
    <>
      <section className="command-header">
        <div>
          <p className="eyebrow">Portfolio command center</p>
          <h1>Five active slots. Weak ideas get replaced.</h1>
          <p className="lead">Each venture must pass all 24 gates. A verified kill boundary archives the venture and restarts its slot at Step 0 with a screened replacement.</p>
        </div>
        <div className="header-actions">
          <div className={`runtime-chip runtime-${snapshot.runtime.status}`}><CircleDot size={14} />Codex {snapshot.runtime.status}</div>
          <button className="secondary-button" onClick={() => void toggleRuntime()} disabled={controlBusy}>
            {snapshot.runtime.portfolioPaused ? <Play size={15} /> : <Pause size={15} />}
            {snapshot.runtime.portfolioPaused ? "Resume portfolio" : "Pause portfolio"}
          </button>
          <button className="icon-button" onClick={refresh} aria-label="Refresh portfolio"><RefreshCw size={17} /></button>
        </div>
      </section>

      <section className="metrics-grid" aria-label="Portfolio metrics">
        <MetricCard label="Gates passed" value={`${port.passedGates}/${port.totalGates}`} note="Across five full paths" />
        <MetricCard label="Evidence records" value={String(port.evidenceCount)} note="Primary proof remains separate" />
        <MetricCard label="Agent queue" value={String(port.activeJobs).padStart(2, "0")} note={`${snapshot.runtime.maxConcurrentJobs} concurrent max`} tone="lime" />
        <MetricCard label="Replacement ready" value={`${port.replacementReady}/5`} note="Only verified kill boundaries count" />
        <MetricCard label="Observed revenue" value={formatCurrency(port.monthlyRevenue)} note="No forecast counted as cash" />
        <MetricCard label="Monthly run cost" value={formatCurrency(port.monthlyCost)} note="Seeded infrastructure estimate" />
      </section>

      <ApprovalCockpit ventures={snapshot.ventures} refreshKey={snapshot.events[0]?.id || ""} />

      <section className="portfolio-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Active pipelines</p>
            <h2>Venture portfolio</h2>
          </div>
          <div className="legend"><span><i className="legend-live" />Active gate</span><span><i className="legend-locked" />Locked</span></div>
        </div>
        <div className="venture-list">
          {snapshot.ventures.map((venture, index) => <VentureRow venture={venture} index={index} replacementReview={snapshot.replacementReviews[venture.id]} key={venture.id} />)}
        </div>
      </section>

      <section className="lower-grid">
        <article className="ops-panel">
          <div className="panel-heading"><span><Activity size={17} />Evidence rail</span><small>Latest activity</small></div>
          <div className="activity-list">
            {snapshot.events.slice(0, 6).map((event) => (
              <div className="activity-item" key={event.id}>
                <span className={`activity-icon activity-${event.severity}`}>{event.severity === "success" ? <Check size={13} /> : <CircleDot size={12} />}</span>
                <div><strong>{event.title}</strong><p>{event.detail}</p></div>
                <time>{relativeTime(event.createdAt)}</time>
              </div>
            ))}
          </div>
        </article>
        <article className="guardrail-panel">
          <div className="guardrail-orbit"><ShieldCheck size={27} /></div>
          <p className="eyebrow">Autonomy boundary</p>
          <h2>Safe research runs. External actions stop.</h2>
          <p>Agents can research public sources and draft internal artifacts. Posting, messaging, purchases, account creation, marketplace edits, and legal conclusions stay outside autopilot.</p>
          <div className="guardrail-stats">
            <span><strong>0</strong> external mutations</span>
            <span><strong>5</strong> active portfolio slots</span>
          </div>
        </article>
      </section>
    </>
  );
}

function AppShell({ snapshot, refresh, logout }: { snapshot: PortfolioSnapshot; refresh: () => void; logout: () => void }) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link to="/" className="brand-lockup"><span className="brand-mark"><Radar size={19} strokeWidth={1.8} /></span><span><strong>XWARM</strong><small>VENTUREGRAPH</small></span></Link>
        <nav aria-label="Primary navigation">
          <Link to="/" className="nav-item nav-active"><Command size={17} /><span>Command</span></Link>
          <a href="#activity" className="nav-item"><Activity size={17} /><span>Activity</span></a>
          <a href="#safeguards" className="nav-item"><ShieldCheck size={17} /><span>Safeguards</span></a>
        </nav>
        <div className="sidebar-ventures">
          <span className="sidebar-label">Pipelines</span>
          {snapshot.ventures.map((venture, index) => (
            <Link to={`/venture/${venture.slug}`} key={venture.id} className="mini-venture">
              <i style={{ background: venture.accent }}>{venture.portfolioSlot || index + 1}</i><span>{venture.name}</span>
            </Link>
          ))}
        </div>
        <div className="sidebar-bottom">
          <div className="autopilot-card"><Sparkles size={16} /><div><strong>Safe autopilot</strong><span>{snapshot.runtime.portfolioPaused ? "Portfolio paused" : snapshot.runtime.autopilotEnabled ? "Scheduled" : "Manual only"}</span></div></div>
          <button className="nav-item logout-button" onClick={logout}><LogOut size={16} /><span>Lock console</span></button>
        </div>
      </aside>
      <main className="main-surface">
        <Routes>
          <Route path="/" element={<PortfolioPage snapshot={snapshot} refresh={refresh} />} />
          <Route path="/venture/:slug" element={<VenturePage definitions={snapshot.framework} onPortfolioRefresh={refresh} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [snapshot, setSnapshot] = useState<PortfolioSnapshot | null>(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const next = await api.snapshot();
      setSnapshot(next);
      setLoadError("");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Could not load portfolio");
    }
  }, []);

  useEffect(() => {
    void api.session().then((session) => setAuthenticated(session.authenticated)).catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    void load();
    const timer = window.setInterval(load, 15_000);
    return () => window.clearInterval(timer);
  }, [authenticated, load]);

  const logout = useCallback(async () => {
    await api.logout();
    setAuthenticated(false);
    setSnapshot(null);
  }, []);

  const ready = useMemo(() => authenticated && snapshot, [authenticated, snapshot]);
  if (authenticated === null) return <LoadingScreen />;
  if (!authenticated) return <LoginScreen onAuthenticated={() => setAuthenticated(true)} />;
  if (!ready) return <LoadingScreen />;
  return (
    <>
      {loadError && <div className="global-error" role="alert">{loadError}</div>}
      <AppShell snapshot={snapshot!} refresh={load} logout={logout} />
    </>
  );
}
